/**
 * Organization structure: companies, departments and teams as a tree, with
 * members and leads. Units structure people; rights come from roles.
 */
import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.js";
import { InputText } from "@primereact/ui/inputtext";
import { Textarea } from "@primereact/ui/textarea";
import { Field } from "../../components/ui/Field.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { CheckboxField, SelectInput } from "../../components/ui/controls.js";
import { cx } from "../../components/ui/cx.js";
import { apiGet, apiPatch, apiPost } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import { ErrorPanel, LoadingPanel, MutationError } from "../shared.js";
import { Panel } from "../commercial/document.js";

interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  status: string;
}

interface OrganizationUnit {
  id: string;
  parentId: string | null;
  name: string;
  description: string | null;
  color: string | null;
  kind: "company" | "department" | "team";
  sortOrder: number;
  active: boolean;
  memberIds: string[];
  leadIds: string[];
  memberCount: number;
  children: OrganizationUnit[];
  version: number;
}

interface Draft {
  id?: string;
  version?: number;
  parentId: string | null;
  name: string;
  description: string;
  color: string;
  kind: OrganizationUnit["kind"];
  active: boolean;
  memberIds: string[];
  leadIds: string[];
}

const KINDS = ["company", "department", "team"] as const;

function flatten(
  units: OrganizationUnit[],
  depth = 0,
): Array<OrganizationUnit & { depth: number }> {
  return units.flatMap((unit) => [
    { ...unit, depth },
    ...flatten(unit.children, depth + 1),
  ]);
}

function descendantIds(unit: OrganizationUnit): string[] {
  return unit.children.flatMap((child) => [child.id, ...descendantIds(child)]);
}

function toDraft(unit: OrganizationUnit): Draft {
  return {
    id: unit.id,
    version: unit.version,
    parentId: unit.parentId,
    name: unit.name,
    description: unit.description ?? "",
    color: unit.color ?? "#0f766e",
    kind: unit.kind,
    active: unit.active,
    memberIds: unit.memberIds,
    leadIds: unit.leadIds,
  };
}

const newDraft = (parentId: string | null): Draft => ({
  parentId,
  name: "",
  description: "",
  color: "#0f766e",
  kind: parentId ? "team" : "company",
  active: true,
  memberIds: [],
  leadIds: [],
});

const userName = (user: UserOption) =>
  `${user.firstName} ${user.lastName}`.trim() || user.email || user.id;

export function TeamSettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canManage = has("teams.manage");
  const units = useQuery({
    queryKey: ["teams"],
    queryFn: () =>
      apiGet<{ data: OrganizationUnit[] }>("/teams").then(({ data }) => data),
  });
  const users = useQuery({
    queryKey: ["users", "organization"],
    queryFn: () =>
      apiGet<{ data: UserOption[] }>("/users", {
        query: { pageSize: 200 },
      }).then(({ data }) => data.filter((user) => user.status === "active")),
    enabled: canManage,
  });
  const flat = useMemo(() => flatten(units.data ?? []), [units.data]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [addMember, setAddMember] = useState("");
  useEffect(() => {
    if (!selectedId) return;
    const unit = flat.find((candidate) => candidate.id === selectedId);
    if (unit) setDraft(toDraft(unit));
  }, [selectedId, flat]);

  const save = useMutation({
    mutationFn: (value: Draft) => {
      const payload = {
        name: value.name.trim(),
        description: value.description.trim() || null,
        color: value.color,
        kind: value.kind,
        parentId: value.parentId,
        active: value.active,
        memberIds: value.memberIds,
        leadIds: value.leadIds.filter((id) => value.memberIds.includes(id)),
      };
      return value.id
        ? apiPatch<{ data: OrganizationUnit }>(`/teams/${value.id}`, {
            ...payload,
            version: value.version,
          })
        : apiPost<{ data: OrganizationUnit }>("/teams", payload);
    },
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ["teams"] });
      setSelectedId(response.data.id);
    },
  });

  if (units.isLoading) return <LoadingPanel />;
  if (units.error) return <ErrorPanel error={units.error} />;
  const selected = flat.find((unit) => unit.id === draft?.id);
  const blockedParents = new Set(
    selected ? [selected.id, ...descendantIds(selected)] : [],
  );
  const userById = new Map((users.data ?? []).map((user) => [user.id, user]));

  return (
    <>
      <PageHeader
        title={t("organization.title")}
        subtitle={t("organization.subtitle")}
        actions={
          canManage ? (
            <Button
              icon="pi pi-plus"
              onClick={() => {
                setSelectedId(null);
                setDraft(newDraft(null));
              }}
            >
              {t("organization.newUnit")}
            </Button>
          ) : null
        }
      />
      <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <Panel>
          {flat.length === 0 ? (
            <p className="text-sm text-app-muted">{t("organization.empty")}</p>
          ) : (
            <ul className="space-y-0.5">
              {flat.map((unit) => (
                <li key={unit.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(unit.id)}
                    style={{ paddingLeft: `${0.75 + unit.depth * 1.25}rem` }}
                    className={cx(
                      "flex w-full items-center gap-2 rounded-lg py-2 pr-3 text-left text-sm",
                      draft?.id === unit.id
                        ? "bg-brand-soft font-medium"
                        : "hover:bg-app-background",
                      !unit.active && "opacity-60",
                    )}
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: unit.color ?? "#94a3b8" }}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 truncate">{unit.name}</span>
                    <span className="text-xs text-app-muted">
                      {unit.memberCount}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        {draft ? (
          <Panel
            title={
              draft.id
                ? draft.name || t("organization.unit")
                : t("organization.newUnit")
            }
            actions={
              draft.id && canManage ? (
                <Button
                  size="small"
                  variant="text"
                  icon="pi pi-plus"
                  onClick={() => {
                    setSelectedId(null);
                    setDraft(newDraft(draft.id!));
                  }}
                >
                  {t("organization.addChild")}
                </Button>
              ) : null
            }
          >
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                save.mutate(draft);
              }}
            >
              <fieldset disabled={!canManage} className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label={t("common.name")} required>
                    <InputText
                      value={draft.name}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setDraft({ ...draft, name: event.target.value })
                      }
                    />
                  </Field>
                  <Field label={t("organization.kind")}>
                    <SelectInput
                      value={draft.kind}
                      onChange={(kind) => setDraft({ ...draft, kind })}
                      options={KINDS.map((kind) => ({
                        value: kind,
                        label: t(`organization.kinds.${kind}`),
                      }))}
                    />
                  </Field>
                  <Field label={t("organization.parent")}>
                    <SelectInput
                      value={draft.parentId ?? ""}
                      onChange={(parentId) =>
                        setDraft({ ...draft, parentId: parentId || null })
                      }
                      options={[
                        { value: "", label: t("organization.noParent") },
                        ...flat
                          .filter((unit) => !blockedParents.has(unit.id))
                          .map((unit) => ({
                            value: unit.id,
                            label: `${"— ".repeat(unit.depth)}${unit.name}`,
                          })),
                      ]}
                    />
                  </Field>
                  <Field label={t("organization.color")}>
                    <input
                      type="color"
                      value={draft.color}
                      onChange={(event) =>
                        setDraft({ ...draft, color: event.target.value })
                      }
                      className="h-10 w-full cursor-pointer rounded-lg border border-app-border bg-app-surface"
                    />
                  </Field>
                </div>
                <Field label={t("organization.description")}>
                  <Textarea
                    value={draft.description}
                    rows={2}
                    autoResize
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                      setDraft({ ...draft, description: event.target.value })
                    }
                  />
                </Field>
                <CheckboxField
                  id="unit-active"
                  checked={draft.active}
                  onChange={(active) => setDraft({ ...draft, active })}
                  label={t("common.active")}
                />
                <div>
                  <h3 className="mb-2 text-sm font-semibold">
                    {t("organization.members")}
                  </h3>
                  {draft.memberIds.length === 0 ? (
                    <p className="text-sm text-app-muted">
                      {t("organization.noMembers")}
                    </p>
                  ) : (
                    <ul className="divide-y divide-app-border rounded-lg border border-app-border">
                      {draft.memberIds.map((id) => {
                        const user = userById.get(id);
                        const lead = draft.leadIds.includes(id);
                        return (
                          <li
                            key={id}
                            className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                          >
                            <span>{user ? userName(user) : id}</span>
                            <span className="flex items-center gap-2">
                              <Button
                                type="button"
                                size="small"
                                variant={lead ? undefined : "outlined"}
                                severity={lead ? undefined : "secondary"}
                                aria-pressed={lead}
                                onClick={() =>
                                  setDraft({
                                    ...draft,
                                    leadIds: lead
                                      ? draft.leadIds.filter((l) => l !== id)
                                      : [...draft.leadIds, id],
                                  })
                                }
                              >
                                {t("organization.lead")}
                              </Button>
                              <Button
                                type="button"
                                size="small"
                                variant="text"
                                severity="danger"
                                icon="pi pi-times"
                                aria-label={t("common.remove")}
                                onClick={() =>
                                  setDraft({
                                    ...draft,
                                    memberIds: draft.memberIds.filter(
                                      (m) => m !== id,
                                    ),
                                    leadIds: draft.leadIds.filter(
                                      (l) => l !== id,
                                    ),
                                  })
                                }
                              />
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {canManage ? (
                    <div className="mt-2 max-w-sm">
                      <SelectInput
                        value={addMember}
                        filter
                        placeholder={t("organization.addMember")}
                        onChange={(id) => {
                          if (id && !draft.memberIds.includes(id))
                            setDraft({
                              ...draft,
                              memberIds: [...draft.memberIds, id],
                            });
                          setAddMember("");
                        }}
                        options={(users.data ?? [])
                          .filter((user) => !draft.memberIds.includes(user.id))
                          .map((user) => ({
                            value: user.id,
                            label: userName(user),
                          }))}
                      />
                    </div>
                  ) : null}
                  <p className="mt-2 text-xs text-app-muted">
                    {t("organization.leadHint")}
                  </p>
                </div>
              </fieldset>
              <MutationError error={save.error} />
              {canManage ? (
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="text"
                    severity="secondary"
                    onClick={() => {
                      setDraft(null);
                      setSelectedId(null);
                    }}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    loading={save.isPending || undefined}
                    disabled={!draft.name.trim()}
                  >
                    {t("common.save")}
                  </Button>
                </div>
              ) : null}
            </form>
          </Panel>
        ) : (
          <Panel>
            <p className="text-sm text-app-muted">
              {t("organization.selectHint")}
            </p>
          </Panel>
        )}
      </div>
    </>
  );
}
